import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../../config';
import './CardRecognitionPage.css';

const CardRecognitionPage = ({
  addToCart,
  userSettings,
  exchangeRates,
  calculatePrice,
  allowedConditions,
  onRecognizedCard
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [recognizedCards, setRecognizedCards] = useState([]);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Debug camera state changes
  useEffect(() => {
    console.log('cameraActive state changed to:', cameraActive);
  }, [cameraActive]);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cardPrices, setCardPrices] = useState({});
  const priceRequests = useRef({});
  const priceCalculator = useRef(calculatePrice);
  priceCalculator.current = calculatePrice;
  useEffect(() => () => { priceRequests.current = {}; }, []);

  // Only the latest selection may publish a quote; API responses can finish out of order.
  const updateCardPrice = useCallback(async (card, condition, printing, language) => {
    const request = {};
    priceRequests.current[card._id] = request;
    setCardPrices(prev => ({ ...prev, [card._id]: { contactForPrice: true } }));
    try {
      const quote = await priceCalculator.current(card, condition, printing, language);
      if (priceRequests.current[card._id] === request) {
        setCardPrices(prev => ({ ...prev, [card._id]: quote }));
      }
    } catch (error) {
      if (priceRequests.current[card._id] === request) {
        setCardPrices(prev => ({ ...prev, [card._id]: { contactForPrice: true } }));
      }
    }
  }, []);

  const [selectedConditions, setSelectedConditions] = useState({});
  const [selectedPrintings, setSelectedPrintings] = useState({});
  const [selectedLanguages, setSelectedLanguages] = useState({});
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      console.log('Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      console.log('Camera stream obtained:', stream);
      
      // Set camera active first to render the video element
      setCameraActive(true);
      console.log('Setting cameraActive to true');

      // Wait for video element to be rendered, then set the stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          console.log('Camera stream set to video element');
          console.log('videoRef.current exists:', !!videoRef.current);

          // Ensure video plays
          videoRef.current.onloadedmetadata = () => {
            console.log('Video metadata loaded, playing video');
            console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            videoRef.current.play().catch(e => console.error('Error playing video:', e));
          };
        } else {
          console.error('videoRef.current is still null after timeout!');
        }
      }, 100); // Small delay to allow React to render the video element
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions or use file upload instead.');
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  }, []);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);
        recognizeCard(blob);
      }
    }, 'image/jpeg', 0.8);
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setCapturedImage(imageUrl);
      recognizeCard(file);
    }
  }, []);

  // Recognize card from image
  const recognizeCard = useCallback(async (imageBlob) => {
    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', imageBlob);

      console.log('Sending card recognition request...');
      const response = await fetch(`${BACKEND_URL}/recognize-card`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Card recognition response:', data);

      if (data.success && data.productId) {
        console.log('Received productId from backend:', data.productId);

        // Use EXACT same process as manual search but stay on card recognition page
        await searchForCardByProductId(data.productId);

      } else {
        setError(data.error || 'No cards recognized in the image. Please try again with a clearer photo.');
        setRecognizedCards([]);
      }
    } catch (err) {
      console.error('Error recognizing card:', err);
      setError('Failed to recognize card. Please try again.');
      setRecognizedCards([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Search for card using productId - search across ALL games without game filter
  const searchForCardByProductId = useCallback(async (productId) => {
    try {
      const url = new URL(`${BACKEND_URL}/catalog`);
      const showNotBuying = localStorage.getItem('showNotBuying') !== null ?
        JSON.parse(localStorage.getItem('showNotBuying')) : true;

      // DO NOT include game parameter - let backend search across all games
      const searchParams = {
        page: '1',
        limit: '25',
        productId: productId.toString(),
        username: userSettings?.username || 'admintcg',
        showBulk: 'true',
        showNotBuying: showNotBuying.toString()
      };
      
      url.search = new URLSearchParams(searchParams).toString();
      console.log('Card recognition fetching from catalog URL (no game filter):', url.toString());

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const catalogData = await response.json();
      console.log('Card recognition received catalog data:', catalogData);

      if (catalogData.items && catalogData.items.length > 0) {
        const foundCard = catalogData.items[0];
        
        // Check if the user has enabled purchasing for this game
        const cardGame = foundCard.gameName;
        const enabledGames = userSettings?.enabledGames || [];
        
        console.log('Card game:', cardGame);
        console.log('User enabled games:', enabledGames);
        
        if (!enabledGames.includes(cardGame)) {
          console.log(`Game ${cardGame} is not enabled for user`);
          setError(`Sorry, we are not currently purchasing ${cardGame} cards. Please contact us if you have questions about our current buying list.`);
          setRecognizedCards([]);
          return;
        }
        
        // Game is enabled, proceed with displaying the card
        setRecognizedCards(catalogData.items);
        
        // Calculate prices for all recognized cards
        for (const card of catalogData.items) {
          if (card.skus && card.skus.length > 0) {
            const defaultCondition = allowedConditions && allowedConditions.length > 0 ? allowedConditions[0] : 'NM';
            const defaultPrinting = card.skus[0]?.printingName || 'Regular';
            const defaultLanguage = 'EN';
            
            
            // Set default selections
            setSelectedConditions(prev => ({ ...prev, [card._id]: defaultCondition }));
            setSelectedPrintings(prev => ({ ...prev, [card._id]: defaultPrinting }));
            setSelectedLanguages(prev => ({ ...prev, [card._id]: defaultLanguage }));
            await updateCardPrice(card, defaultCondition, defaultPrinting, defaultLanguage);
          }
        }
        
        console.log('Card recognition completed - found card');
      } else {
        console.log('Card recognition: No matching cards found');
        setError('Card not found in our catalog. Please try again.');
        setRecognizedCards([]);
      }
    } catch (error) {
      console.error('Error searching for card by productId:', error);
      setError('Failed to search for card. Please try again.');
      setRecognizedCards([]);
    }
  }, [updateCardPrice, allowedConditions, userSettings]);


  // Add recognized card to cart - EXACT same logic as manual search
  const handleAddToCart = useCallback((card, isCash) => {
    const condition = selectedConditions[card._id] || 'NM';
    const printing = selectedPrintings[card._id] || (card.skus && card.skus[0] ? card.skus[0].printingName : 'Regular');
    const language = selectedLanguages[card._id] || 'EN';
    const quantity = 1;
    
    // Get the price data for the selected language
    const cardPriceData = cardPrices[card._id];
    if (!cardPriceData) {
      console.error('Price not found for card:', card._id);
      return;
    }

    const currentPrice = cardPriceData;
    const basePrice = currentPrice.basePrice;
    const offeredPrice = isCash ? currentPrice.cashPrice : currentPrice.creditPrice;
    
    // For non-English cards, we need to find the equivalent EN SKU
    // but add a note that it's actually a non-English card
    let sku;
    let orderNote = '';
    
    if (language !== 'EN') {
      // First try to find the exact SKU with the selected language
      const exactSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
      
      // Then find the equivalent EN SKU
      const enSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === 'EN'
      );
      
      // If we have an EN SKU, use it but add a note
      if (enSku) {
        sku = enSku;
        // Include the percentage in the note if it's a derived price
        if (currentPrice.isDerivedPrice) {
          orderNote = `This ${card.name} is actually a ${language} language card (${currentPrice.derivedPercentage}% of EN price).`;
        } else {
          orderNote = `This ${card.name} is actually a ${language} language card.`;
        }
      } else {
        // If no EN SKU exists, use the exact language SKU if available
        sku = exactSku;
      }
    } else {
      // For English cards, just find the matching SKU
      sku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
    }

    console.log('Adding to cart from card recognition:', {
      name: card.name,
      condition,
      printing,
      language,
      basePrice,
      offeredPrice,
      isCash,
      skuId: sku?.skuId,
      isDerivedPrice: currentPrice.isDerivedPrice,
      derivedPercentage: currentPrice.derivedPercentage,
      orderNote
    });

    // Create a nonEnglish field for non-English cards
    let nonEnglish = null;
    if (language !== 'EN') {
      nonEnglish = {
        language,
        offeredPrice,
        isDerivedPrice: currentPrice.isDerivedPrice,
        derivedPercentage: currentPrice.derivedPercentage,
        basePrice
      };
    }

    // Pass the actual selected language, order note, and nonEnglish data to addToCart
    // Use EXACT same signature as manual search
    addToCart(card, condition, printing, quantity, sku?.skuId, isCash, language, orderNote, nonEnglish);
  }, [addToCart, cardPrices, selectedConditions, selectedPrintings, selectedLanguages]);

  // Clear results and start over
  const clearResults = useCallback(() => {
    setRecognizedCards([]);
    setCapturedImage(null);
    setError(null);
    priceRequests.current = {};
    setCardPrices({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const userCurrency = userSettings?.currency || 'USD';
  const currencySymbol = currencySymbols[userCurrency] || '$';

  return (
    <div className="card-recognition-page">
      <div className="card-recognition-header">
        <p>{t('Take a photo or upload an image of your cards to get instant pricing')}</p>
      </div>

      <div className="card-recognition-controls">
        <div className="camera-controls">
          {!cameraActive && (
            <button
              className="btn btn-primary"
              onClick={startCamera}
              disabled={isLoading}
            >
              {t('Start Camera')}
            </button>
          )}
        </div>

        {/* Camera active controls - always show when camera is active */}
        {cameraActive && (
          <div className="camera-active-controls">
            <button 
              className="btn btn-success"
              onClick={capturePhoto}
              disabled={isLoading}
            >
              {t('Capture Photo')}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={stopCamera}
              disabled={isLoading}
            >
              {t('Stop Camera')}
            </button>
          </div>
        )}

        <div className="file-upload-controls">
          <label className="btn btn-outline">
            {t('Upload Image')}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={isLoading}
            />
          </label>
        </div>

        {(recognizedCards.length > 0 || capturedImage) && (
          <button 
            className="btn btn-outline"
            onClick={clearResults}
            disabled={isLoading}
          >
            {t('Clear & Start Over')}
          </button>
        )}
      </div>

      {/* Camera Video - Always show when camera is active, regardless of permission */}
      {console.log('Rendering: cameraActive =', cameraActive)}
      {cameraActive && (
        <div className="camera-container">
          {console.log('Rendering camera container')}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-video"
            style={{ border: '2px solid red' }} // Debug border
            onLoadedMetadata={() => {
              console.log('Video metadata loaded, video dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
            }}
            onError={(e) => {
              console.error('Video error:', e);
            }}
            onCanPlay={() => {
              console.log('Video can play');
            }}
          />
        </div>
      )}
      {!cameraActive && console.log('Camera not active, not rendering video')}

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Captured Image Preview */}
      {capturedImage && (
        <div className="captured-image-container">
          <h3>{t('Captured Image')}</h3>
          <img src={capturedImage} alt="Captured card" className="captured-image" />
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="recognition-loading">
          <div className="spinner"></div>
          <p>{t('Recognizing cards...')}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="recognition-error">
          <p>{error}</p>
        </div>
      )}

      {/* Recognized Cards - Improved UI */}
      {recognizedCards.length > 0 && (
        <div className="recognized-cards">
          <div className="recognized-card-match">
            {recognizedCards.map((card, index) => {
              const priceData = cardPrices[card._id] || { cashPrice: 0, creditPrice: 0, contactForPrice: true };
              
              return (
                <div key={index} className="card-match-container">
                  <div className="match-header">
                    <h3>{t('Card Found!')}</h3>
                    <button 
                      className="add-another-button"
                      onClick={clearResults}
                    >
                      {t('Add Another')}
                    </button>
                  </div>
                  
                  <div className="recognized-card">
                    <img 
                      src={card.image || "/No-Image-Placeholder.svg.png"} 
                      alt={card.name} 
                      className="recognized-card-image" 
                    />
                    <div className="recognized-card-info">
                      <h4>{card.name}</h4>
                      <p className="card-set">{card.expansionName}</p>
                      <p className="card-number">#{card.number}</p>

                      {/* Condition and Printing Selectors */}
                      {card.skus && card.skus.length > 0 && (
                        <div className="card-selectors">
                          <div className="selector-group">
                            <label>{t('Condition')}:</label>
                            <select
                              value={selectedConditions[card._id] || 'NM'}
                              onChange={(e) => {
                                const newCondition = e.target.value;
                                setSelectedConditions(prev => ({
                                  ...prev,
                                  [card._id]: newCondition
                                }));
                                updateCardPrice(card, newCondition, selectedPrintings[card._id] || card.skus[0]?.printingName, selectedLanguages[card._id] || 'EN');
                              }}
                            >
                              {(() => {
                                const allConditions = [...new Set(card.skus.map(sku => sku.condAbbr))];
                                const filteredConditions = allowedConditions && allowedConditions.length > 0
                                  ? allConditions.filter(condition => allowedConditions.includes(condition))
                                  : allConditions;
                                return filteredConditions.map(condition => (
                                  <option key={condition} value={condition}>
                                    {condition}
                                  </option>
                                ));
                              })()}
                            </select>
                          </div>

                          <div className="selector-group">
                            <label>{t('Printing')}:</label>
                            <select
                              value={selectedPrintings[card._id] || card.skus[0]?.printingName}
                              onChange={(e) => {
                                const newPrinting = e.target.value;
                                setSelectedPrintings(prev => ({
                                  ...prev,
                                  [card._id]: newPrinting
                                }));
                                updateCardPrice(card, selectedConditions[card._id] || 'NM', newPrinting, selectedLanguages[card._id] || 'EN');
                              }}
                            >
                              {[...new Set(card.skus.map(sku => sku.printingName))].map(printing => (
                                <option key={printing} value={printing}>
                                  {printing}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="selector-group">
                            <label>{t('Language')}:</label>
                            <select
                              value={selectedLanguages[card._id] || 'EN'}
                              onChange={(e) => {
                                const newLanguage = e.target.value;
                                setSelectedLanguages(prev => ({
                                  ...prev,
                                  [card._id]: newLanguage
                                }));
                                updateCardPrice(card, selectedConditions[card._id] || 'NM', selectedPrintings[card._id] || card.skus[0]?.printingName, newLanguage);
                              }}
                            >
                              {[...new Set(card.skus.map(sku => sku.langAbbr))].map(language => (
                                <option key={language} value={language}>
                                  {language}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      <div className="card-actions">
                        {priceData.contactForPrice ? (
                          <button 
                            className="add-to-cart-button contact-for-price amber-bg"
                            onClick={() => {
                              console.log('Contact for price requested for:', card.name);
                            }}
                          >
                            {t('Contact for Price')}
                          </button>
                        ) : priceData.isBulk ? (
                          <button className="add-to-cart-button bulk">
                            {t('Bulk Item')}
                          </button>
                        ) : (
                          <>
                            {priceData.creditPrice > 0 && (
                              <button 
                                className="add-to-cart-button credit"
                                onClick={() => handleAddToCart(card, false)}
                              >
                                {t('Credit')} {currencySymbol}{(priceData.creditPrice || 0).toFixed(2)}
                                {priceData.isDerivedPrice && (
                                  <span className="derived-price-indicator" title={`${t('Price calculated as')} ${priceData.derivedPercentage}% ${t('of EN price')}`}>*</span>
                                )}
                              </button>
                            )}
                            {priceData.cashPrice > 0 ? (
                              <button 
                                className="add-to-cart-button cash"
                                onClick={() => handleAddToCart(card, true)}
                              >
                                {t('Cash')} {currencySymbol}{(priceData.cashPrice || 0).toFixed(2)}
                                {priceData.isDerivedPrice && (
                                  <span className="derived-price-indicator" title={`${t('Price calculated as')} ${priceData.derivedPercentage}% ${t('of EN price')}`}>*</span>
                                )}
                              </button>
                            ) : (
                              <button className="add-to-cart-button not-buying" disabled>
                                {t('Not Buying Cash')}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardRecognitionPage;
